import {
  holdsId,
  type VolumeForecastType,
  type VolumeSourcePickerData,
  type VolumeSourceSelection,
} from 'components/volume-source-picker';
import { type VOLUME_OVERRIDE_FIELDS } from './slot-tearsheet/constants';
import type { SlotInputNode } from 'types/packages-api/slot-valuation-inputs';
import type { SlotOverrideLogEntry, SlotRow } from './types';

/**
 * The override a volume-source save writes: the modal's selection, with every key the revert
 * list (VOLUME_OVERRIDE_FIELDS) names. The selection's three optional fields are defaulted to
 * undefined BEFORE the spread, so a switch from a decline to a type curve clears the
 * decline's forecast source and DCA scenario on the painted row rather than leaving them —
 * and a key added to the revert list but not here is a compile error.
 *
 * @param {VolumeSourceSelection} selection - The chosen forecast and its fields.
 * @return {object} The override fields, one per revert-list key.
 */
export const volumeOverrides = (
  selection: VolumeSourceSelection
): Record<(typeof VOLUME_OVERRIDE_FIELDS)[number], string | undefined> => ({
  volumeSourceHref: undefined,
  forecastSource: undefined,
  dcaScenario: undefined,
  ...selection,
});

/**
 * What the inline Edit Volume Source modal knows about the row's own source beyond its id —
 * enough to show it as an option when the row's own list does not contain it (INS-13809).
 * Every field may be missing; the option degrades to the bare id on the type-curve tab.
 */
export interface CurrentVolumeSource {
  /** The display name: a DCA forecast's business id, else the id. */
  name?: string | null;
  /** Which tab the option belongs on. */
  type?: VolumeForecastType | null;
  /** A type curve's detail link. */
  href?: string | null;
}

/**
 * Narrows a loose value to a forecast type the picker lists. Anything else — absent, or a
 * `composite_wellbore` recipe — is not one.
 *
 * @param {unknown} value - A candidate type.
 * @return {VolumeForecastType | null} The type, or null.
 */
const asForecastType = (value: unknown): VolumeForecastType | null =>
  value === 'type_curve' || value === 'pdp_decline' ? value : null;

/**
 * Maps the backend's forecast-status vocabulary (`ConfigurationHelpers::
 * VOLUME_SOURCE_TYPE_TO_FORECAST_STATUS`: 'type-curve' / 'pdp') back to a picker type. An
 * override of the volume source rewrites the slot's forecast status with it, so the status
 * follows the effective source.
 *
 * @param {string | null | undefined} status - The row's forecast status.
 * @return {VolumeForecastType | null} The type, or null for any other value.
 */
export const forecastTypeFromStatus = (
  status: string | null | undefined
): VolumeForecastType | null => {
  if (status === 'type-curve') return 'type_curve';
  if (status === 'pdp') return 'pdp_decline';
  return null;
};

/**
 * The type an override recorded for this source id, newest first: the pending log (camelCase
 * operations), then the committed log (stored wire entries, whose `overrides` keys may be
 * snake_case). Matched on the source id rather than on the slot: a forecast's type belongs
 * to the forecast, so any entry that set this id says what it is — including a bulk or
 * scoped entry whose slot membership the client cannot resolve.
 *
 * @param {string} sourceId - The row's source id.
 * @param {SlotOverrideLogEntry[]} pending - The working config's pending operations.
 * @param {Array<Record<string, unknown>>} committed - The committed wire entries.
 * @return {VolumeForecastType | null} The recorded type, or null when none matches.
 */
export function overrideTypeForSource(
  sourceId: string,
  pending: SlotOverrideLogEntry[],
  committed: Record<string, unknown>[]
): VolumeForecastType | null {
  const entries: unknown[] = [...pending].reverse();
  entries.push(...[...committed].reverse());
  for (const entry of entries) {
    const overrides =
      entry && typeof entry === 'object' && 'overrides' in entry
        ? (entry.overrides as Record<string, unknown> | undefined)
        : undefined;
    if (!overrides) continue;
    const id = overrides.volumeSourceId ?? overrides.volume_source_id;
    // A stored id may be numeric (a DCA forecast's database id).
    if (typeof id !== 'string' && typeof id !== 'number') continue;
    if (String(id) !== sourceId) continue;
    const type = asForecastType(
      overrides.volumeSourceType ?? overrides.volume_source_type
    );
    if (type) return type;
  }
  return null;
}

/**
 * The tearsheet's volume source as the inline modal pins it. The node states its kind as
 * `valueSourceType` (node_binder.rb #volume_source_kind), including for a pending override,
 * and that answer is taken as given — a null included. Only a payload served before that key
 * existed falls back to reading the kind out of the link's path: a type curve links to
 * `/type_curves/…`, a decline to its well's page (`/wells/…`). Without a link the run's
 * forecast status says which kind — unless the value is a pending change (the node carries a
 * `runValue`), where that status still describes the run's source, not this one.
 *
 * A DCA decline's id comes with its name as `valueLabel` (INS-13307), so the pinned option reads
 * by name; anything without one is pinned by its id.
 *
 * @param {SlotInputNode} node - The tearsheet's Volume Source node.
 * @param {string | null | undefined} forecastStatus - The run's forecast status.
 * @return {CurrentVolumeSource} The type, href and (when served) name the modal can show.
 */
export function currentVolumeSourceForNode(
  node: Pick<
    SlotInputNode,
    'valueHref' | 'valueSourceType' | 'valueLabel' | 'values'
  >,
  forecastStatus: string | null | undefined
): CurrentVolumeSource {
  const href = node.valueHref ?? null;
  const type =
    node.valueSourceType !== undefined
      ? node.valueSourceType
      : typeFromLink(href, node, forecastStatus);
  return {
    href: type === 'type_curve' ? href : null,
    type,
    ...(node.valueLabel ? { name: node.valueLabel } : {}),
  };
}

/**
 * The pre-`valueSourceType` reading: the kind from the link's path, else the run's status.
 *
 * @param {string | null} href - The node's link.
 * @param {SlotInputNode} node - The node, for whether its value is a pending change.
 * @param {string | null | undefined} forecastStatus - The run's forecast status.
 * @return {VolumeForecastType | null} The kind, or null when it cannot be established.
 */
function typeFromLink(
  href: string | null,
  node: Pick<SlotInputNode, 'values'>,
  forecastStatus: string | null | undefined
): VolumeForecastType | null {
  if (href?.startsWith('/type_curves/')) return 'type_curve';
  if (href?.startsWith('/wells/')) return 'pdp_decline';
  const pending = node.values?.[0]?.runValue !== undefined;
  return pending ? null : forecastTypeFromStatus(forecastStatus);
}

/**
 * The row's own source as the inline modal pins it. The type comes, in order, from the row's
 * `volumeSourceType` (emitted by insights_ltd_slots.rb, but there it usually holds the
 * pipeline version or the composite marker, which `asForecastType` drops), from an override
 * in the working config that set this id, then from the row's forecast status. Left null
 * past that, and the modal falls back to Type Curve.
 *
 * @param {SlotRow} row - The row being edited.
 * @param {SlotOverrideLogEntry[]} pending - The working config's pending operations.
 * @param {Array<Record<string, unknown>>} committed - The committed wire entries.
 * @return {CurrentVolumeSource} The name, type and href the modal can show.
 */
export function currentVolumeSourceForRow(
  row: SlotRow,
  pending: SlotOverrideLogEntry[],
  committed: Record<string, unknown>[]
): CurrentVolumeSource {
  const sourceId = row.volumeSourceId ?? null;
  return {
    name: row.volumeSourceName ?? null,
    href: row.volumeSourceHref ?? null,
    type:
      asForecastType(row.volumeSourceType) ??
      (sourceId ? overrideTypeForSource(sourceId, pending, committed) : null) ??
      forecastTypeFromStatus(row.forecastStatus),
  };
}

/**
 * Which list the inline picker opens on for a row: the one holding its source. With no type
 * known, a well's slot opens on its declines and any other slot (an LTD or composite place,
 * which has none) on its area's type curves.
 *
 * @param {boolean} isWellSlot - Whether the slot is a well, as the backend reports it
 *   (`SlotKeyBuilder#uwi_slot_id?`).
 * @param {CurrentVolumeSource} [current] - What is known about the row's source.
 * @return {VolumeForecastType} The list to open on.
 */
export const opensOnFor = (
  isWellSlot: boolean,
  current?: CurrentVolumeSource
): VolumeForecastType =>
  current?.type ?? (isWellSlot ? 'pdp_decline' : 'type_curve');

/**
 * The row's own forecast lists with its current source added when they lack it, so the
 * source renders selected (and tagged as the one in use) instead of leaving the box empty.
 * That happens when the source was copied from another area or well: the row's own endpoint
 * does not add a forecast it would not otherwise list. The pinned option is not `isCurrent`,
 * which on a decline means its well's latest.
 *
 * The option carries no forecast source or DCA scenario — the row does not have them. That
 * is safe because it only ever stands for the row's existing source, and Save stays off
 * while the row's source is the selection, so its missing routing fields are never sent.
 *
 * With no type known, the option goes in the type curves; the inline modal always passes
 * the list it opens on (`opensOnFor`).
 *
 * @param {VolumeSourcePickerData['forecasts']} forecasts - The row's own lists.
 * @param {string | null} sourceId - The row's source id.
 * @param {CurrentVolumeSource} [current] - What is known about that source.
 * @return {VolumeSourcePickerData['forecasts']} The lists, with the source pinned if needed.
 */
export function withPinnedSource(
  forecasts: VolumeSourcePickerData['forecasts'],
  sourceId: string | null,
  current?: CurrentVolumeSource
): VolumeSourcePickerData['forecasts'] {
  if (!forecasts || !sourceId) return forecasts;
  if (
    holdsId(forecasts.typeCurves, sourceId) ||
    holdsId(forecasts.pdpDeclines, sourceId)
  ) {
    return forecasts;
  }
  const name = current?.name || sourceId;
  if (current?.type === 'pdp_decline') {
    return {
      ...forecasts,
      pdpDeclines: [{ id: sourceId, name }, ...forecasts.pdpDeclines],
    };
  }
  return {
    ...forecasts,
    typeCurves: [
      {
        id: sourceId,
        name,
        href: current?.href ?? undefined,
      },
      ...forecasts.typeCurves,
    ],
  };
}
